import React from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { FileTextIcon, CalendarIcon, MailIcon, CopyIcon, PlayIcon, ScaleIcon, DollarSignIcon, ListIcon, ChevronRightIcon, UsersIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, CheckCircleIcon, CircleIcon, TagIcon, PlusIcon, ShieldIcon } from 'lucide-react';
import { ISSUE_COLORS } from '../components/review/types';
interface CaseDashboardScreenProps {
  onLogout: () => void;
  onNavigateCases: () => void;
  onStartReview: () => void;
  onNavigateMembers: () => void;
  onNavigateAdmin: () => void;
}
export function CaseDashboardScreen({
  onLogout,
  onNavigateCases,
  onStartReview,
  onNavigateMembers,
  onNavigateAdmin
}: CaseDashboardScreenProps) {
  // Mock stats for the dashboard
  const stats = {
    totalDocs: 2847,
    dateRange: 'Jul 10, 2023 — Sep 15, 2023',
    emailThreads: 142,
    duplicateGroups: 89
  };
  // Mock review status breakdown (mutually exclusive, adds to totalDocs)
  const reviewBreakdown = [{
    status: 'Hot',
    count: 47,
    color: 'bg-red-500',
    textColor: 'text-red-400',
    icon: FlameIcon
  }, {
    status: 'Critical',
    count: 124,
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    icon: AlertOctagonIcon
  }, {
    status: 'TBD',
    count: 38,
    color: 'bg-yellow-500',
    textColor: 'text-yellow-400',
    icon: HelpCircleIcon
  }, {
    status: 'Reviewed',
    count: 683,
    color: 'bg-green-500',
    textColor: 'text-green-400',
    icon: CheckCircleIcon
  }, {
    status: 'Not Reviewed',
    count: 1955,
    color: 'bg-gray-600',
    textColor: 'text-gray-400',
    icon: CircleIcon
  }];
  const totalForBar = reviewBreakdown.reduce((sum, r) => sum + r.count, 0);
  const reviewedTotal = reviewBreakdown.filter(r => r.status !== 'Not Reviewed').reduce((sum, r) => sum + r.count, 0);
  const reviewPct = Math.round(reviewedTotal / totalForBar * 100);
  // Mock case issues
  const caseIssues = [{
    id: 'issue-1',
    label: 'Lease Agreement',
    color: ISSUE_COLORS[0],
    count: 342
  }, {
    id: 'issue-2',
    label: 'Zoning / Land Use',
    color: ISSUE_COLORS[1],
    count: 187
  }, {
    id: 'issue-3',
    label: 'Environmental',
    color: ISSUE_COLORS[2],
    count: 94
  }, {
    id: 'issue-4',
    label: 'Title Defect',
    color: ISSUE_COLORS[3],
    count: 56
  }, {
    id: 'issue-5',
    label: 'Financial Damages',
    color: ISSUE_COLORS[4],
    count: 218
  }];
  // Mock document type breakdown
  const typeBreakdown = [{
    type: 'Emails',
    count: 1842,
    color: 'bg-blue-500',
    width: '65%'
  }, {
    type: 'Contracts',
    count: 323,
    color: 'bg-green-500',
    width: '11%'
  }, {
    type: 'Reports',
    count: 412,
    color: 'bg-teal-500',
    width: '14%'
  }, {
    type: 'Invoices',
    count: 168,
    color: 'bg-amber',
    width: '6%'
  }, {
    type: 'Other',
    count: 102,
    color: 'bg-gray-500',
    width: '4%'
  }];
  return <div className="min-h-screen flex flex-col bg-navy" data-id="element-523" data-mp="z57u2xmcwn98">
      <Header breadcrumbs={[{
      label: 'Cases',
      onClick: onNavigateCases
    }, {
      label: 'Smith v. Acme Corp'
    }]} documentCount={stats.totalDocs} onLogout={onLogout} data-id="element-524" data-mp="z2lnp1b7cdsl" />

      <main className="flex-1 overflow-y-auto p-8" data-id="element-525" data-mp="1mtr4pjsvpc4h">
        <div className="max-w-6xl mx-auto space-y-10" data-id="element-526" data-mp="1ms67fmf6jf2x">
          {/* Header Section */}
          <div className="flex items-start justify-between" data-id="element-527" data-mp="1huef4ramhqy7">
            <div data-id="element-528" data-mp="bfdjkmpifas5">
              <h1 className="text-3xl font-serif text-white mb-2" data-id="element-529" data-mp="bfwxztfybh1f">
                Case Overview
              </h1>
              <p className="text-gray-400" data-id="element-530" data-mp="6e7o4h4gt9ex">
                High-level summary and starting points for Smith v. Acme Corp.
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-531" data-mp="2016r70pajlb5">
              <Button variant="outline" onClick={onNavigateAdmin} data-id="element-532" data-mp="201zrpjxnpeku">
                <ShieldIcon className="w-4 h-4 mr-2" data-id="element-533" data-mp="1lctrbsn31ibd" />
                Admin Panel
              </Button>
              <Button variant="outline" onClick={onNavigateMembers} data-id="element-534" data-mp="1qbn6dpi0phdn">
                <UsersIcon className="w-4 h-4 mr-2" data-id="element-535" data-mp="2dzwp2ijxpp0k" />
                Team Members
              </Button>
            </div>
          </div>

          {/* Key Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-536" data-mp="2ix6kqfxx2wwu">
            <Card className="p-5 flex flex-col justify-center" data-id="element-537" data-mp="2420athvpady8">
              <div className="flex items-center gap-3 mb-2" data-id="element-538" data-mp="290bpc5wxxcms">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-539" data-mp="sn3edmx6smgu">
                  <FileTextIcon className="w-5 h-5" data-id="element-540" data-mp="sp7mkh24c7mx" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-541" data-mp="xnrauo46pvig">
                  Total Documents
                </span>
              </div>
              <div className="text-3xl font-serif text-white mt-1" data-id="element-542" data-mp="36vnplbcjqmnt">
                {stats.totalDocs.toLocaleString()}
              </div>
            </Card>

            <Card className="p-5 flex flex-col justify-center" data-id="element-543" data-mp="3ksaymchrx3a3">
              <div className="flex items-center gap-3 mb-2" data-id="element-544" data-mp="3gtjzy0xntdkv">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-545" data-mp="2t2y55bco1pst">
                  <CalendarIcon className="w-5 h-5" data-id="element-546" data-mp="2o4d459plb23b" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-547" data-mp="2o2a2fsf2rkir">
                  Date Range
                </span>
              </div>
              <div className="text-lg font-medium text-white mt-1 leading-tight" data-id="element-548" data-mp="2x4037ouujvt7">
                {stats.dateRange}
              </div>
            </Card>

            <Card className="p-5 flex flex-col justify-center" data-id="element-549" data-mp="25dvddaobigkl">
              <div className="flex items-center gap-3 mb-2" data-id="element-550" data-mp="25ddgqcxqu0c5">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-551" data-mp="8e0xju1ijce5">
                  <MailIcon className="w-5 h-5" data-id="element-552" data-mp="dc2eo9fdp1mz" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-553" data-mp="da94ik5k3za8">
                  Email Threads
                </span>
              </div>
              <div className="text-3xl font-serif text-white mt-1" data-id="element-554" data-mp="n0u3i8p575to">
                {stats.emailThreads.toLocaleString()}
              </div>
            </Card>

            <Card className="p-5 flex flex-col justify-center" data-id="element-555" data-mp="3qily7uuny3vk">
              <div className="flex items-center gap-3 mb-2" data-id="element-556" data-mp="3qkygk94c2g95">
                <div className="p-2 bg-navy-lighter rounded-lg text-gray-400" data-id="element-557" data-mp="3vgx988wp2kfd">
                  <CopyIcon className="w-5 h-5" data-id="element-558" data-mp="4c70rwufusj9" />
                </div>
                <span className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-559" data-mp="49b389mi65vc">
                  Duplicate Groups
                </span>
              </div>
              <div className="text-3xl font-serif text-white mt-1" data-id="element-560" data-mp="1fozngt8dgqsg">
                {stats.duplicateGroups.toLocaleString()}
              </div>
            </Card>
          </div>

          {/* Document Type Breakdown */}
          <Card className="p-6" data-id="element-561" data-mp="2vc4mnsk0zc8p">
            <h2 className="text-lg font-serif text-white mb-6" data-id="element-562" data-mp="2qiig8i4gg4lc">
              Document Types
            </h2>

            {/* Stacked Bar */}
            <div className="h-6 w-full flex rounded-full overflow-hidden mb-6 bg-navy-lighter" data-id="element-563" data-mp="2ljp3irwzshm8">
              {typeBreakdown.map(item => <div key={item.type} className={`h-full ${item.color} border-r border-navy last:border-r-0 transition-all duration-500 hover:opacity-90`} style={{
              width: item.width
            }} title={`${item.type}: ${item.count.toLocaleString()}`} data-id="element-564" data-mp="2cjitjg0i5zrt" />)}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-8 gap-y-4" data-id="element-565" data-mp="urnrzp3hbok">
              {typeBreakdown.map(item => <div key={item.type} className="flex items-center gap-2" data-id="element-566" data-mp="35v30bxd2foe1">
                  <div className={`w-3 h-3 rounded-full ${item.color}`} data-id="element-567" data-mp="3at4img2o3jog" />
                  <div data-id="element-568" data-mp="2w3ym4zv7s7k9">
                    <span className="text-sm text-gray-300 font-medium mr-2" data-id="element-569" data-mp="2w4qjog8dgfd6">
                      {item.type}
                    </span>
                    <span className="text-sm text-gray-500" data-id="element-570" data-mp="3118lp8cbhzlt">
                      {item.count.toLocaleString()}
                    </span>
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Review Progress */}
          <Card className="p-6" data-id="element-571" data-mp="2d65w2y5ovs4a">
            <div className="flex items-center justify-between mb-6" data-id="element-572" data-mp="2d6xef7a9ztcm">
              <h2 className="text-lg font-serif text-white" data-id="element-573" data-mp="2i58sju7lsq0n">Review Progress</h2>
              <div className="text-sm text-gray-400" data-id="element-574" data-mp="bd7uactxpp77">
                <span className="text-white font-semibold" data-id="element-575" data-mp="bbn0wqjcwcax">{reviewPct}%</span>{' '}
                reviewed
                <span className="text-gray-500 ml-1" data-id="element-576" data-mp="gagfkmadq9co">
                  ({reviewedTotal.toLocaleString()} of{' '}
                  {totalForBar.toLocaleString()})
                </span>
              </div>
            </div>

            {/* Stacked Bar */}
            <div className="h-6 w-full flex rounded-full overflow-hidden mb-6 bg-navy-lighter" data-id="element-577" data-mp="2alg9f7jlp98n">
              {reviewBreakdown.map(item => {
              const pct = item.count / totalForBar * 100;
              if (pct < 0.5) return null;
              return <div key={item.status} className={`h-full ${item.color} border-r border-navy/50 last:border-r-0 transition-all duration-500`} style={{
                width: `${pct}%`
              }} title={`${item.status}: ${item.count.toLocaleString()}`} data-id="element-578" data-mp="2fkat0bpe71tt" />;
            })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-6 gap-y-3" data-id="element-579" data-mp="u8j1k7ttds2w">
              {reviewBreakdown.map(item => {
              const Icon = item.icon;
              const pct = (item.count / totalForBar * 100).toFixed(1);
              return <div key={item.status} className="flex items-center gap-2" data-id="element-580" data-mp="1n7v7o74lpjna">
                    <Icon className={`w-3.5 h-3.5 ${item.textColor}`} data-id="element-581" data-mp="1j7b9r1c7ec03" />
                    <span className="text-sm text-gray-300 font-medium" data-id="element-582" data-mp="1j94l2txrhkef">
                      {item.status}
                    </span>
                    <span className="text-sm text-gray-500" data-id="element-583" data-mp="2axpjcld6p25y">
                      {item.count.toLocaleString()}
                    </span>
                    <span className="text-[10px] text-gray-600" data-id="element-584" data-mp="265y0yy46m2qw">({pct}%)</span>
                  </div>;
            })}
            </div>
          </Card>

          {/* Case Issues */}
          <Card className="p-6" data-id="element-585" data-mp="2ypd3mkfxxez9">
            <div className="flex items-center justify-between mb-6" data-id="element-586" data-mp="2ypmx4i07249p">
              <div data-id="element-587" data-mp="2tvij5esigvz4">
                <h2 className="text-lg font-serif text-white flex items-center gap-2" data-id="element-588" data-mp="2tv8upvn46wvj">
                  <TagIcon className="w-5 h-5 text-gray-400" data-id="element-589" data-mp="gga7zn10pb04" />
                  Case Issues
                </h2>
                <p className="text-sm text-gray-400 mt-1" data-id="element-590" data-mp="lfdbh31cn95z">
                  Predefined tags available for reviewers to apply to documents.
                </p>
              </div>
              <Button variant="outline" className="gap-2" data-id="element-591" data-mp="ufi76fx6kj4b">
                <PlusIcon className="w-4 h-4" data-id="element-592" data-mp="ze37efcfn2sr" />
                Add Issue
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-593" data-mp="14bcx7w3qhgh4">
              {caseIssues.map(issue => <div key={issue.id} className="flex items-center justify-between p-3 rounded-lg border border-navy-lighter bg-navy-light/50 hover:bg-navy-light transition-colors" data-id="element-594" data-mp="193wgn9p3r5tu">
                  <div className="flex items-center gap-3" data-id="element-595" data-mp="1n2mfouvhffku">
                    <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: issue.color
                }} data-id="element-596" data-mp="1n35n3hnialkm" />
                    <span className="text-sm font-medium text-gray-200" data-id="element-597" data-mp="3e4uvtbvwv7ff">
                      {issue.label}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 bg-navy px-2 py-1 rounded-md border border-navy-lighter" data-id="element-598" data-mp="3o42emelezu9x">
                    {issue.count} docs
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Quick Start Actions */}
          <div data-id="element-599" data-mp="fpk4m7bwy4no">
            <h2 className="text-2xl font-serif text-white mb-6" data-id="element-600" data-mp="fnzagitignqc">Quick Start</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-601" data-mp="apxrryzzyqfe">
              {/* Action 1 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-602" data-mp="2hfvl7q7uttk2">
                <div className="p-6 flex-1" data-id="element-603" data-mp="2cjx1wh1dv1qi">
                  <div className="flex items-start justify-between mb-4" data-id="element-604" data-mp="s0kt9ey4kmnk">
                    <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400 group-hover:bg-blue-500/20 transition-colors" data-id="element-605" data-mp="wycu4ygqzj3w">
                      <PlayIcon className="w-6 h-6" data-id="element-606" data-mp="i8oxwbnfban1" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-607" data-mp="n8b5o5khfyog">
                      87 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-608" data-mp="e7v9zi9zdvu5">
                    Review terminal emails
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-609" data-mp="3vimld9r0su56">
                    Start with the final emails in each thread. This is the most
                    efficient way to grasp the full conversation without reading
                    redundant replies.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-610" data-mp="3uquv8sbjfcv6">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-611" data-mp="3lqg5ngrjjblv" />
                </div>
              </Card>

              {/* Action 2 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-612" data-mp="3gqab9stvzb03">
                <div className="p-6 flex-1" data-id="element-613" data-mp="371k1vvwiokr2">
                  <div className="flex items-start justify-between mb-4" data-id="element-614" data-mp="370ha7lrdeea3">
                    <div className="p-3 bg-green-500/10 rounded-xl text-green-400 group-hover:bg-green-500/20 transition-colors" data-id="element-615" data-mp="32zf9f0ht1u60">
                      <ScaleIcon className="w-6 h-6" data-id="element-616" data-mp="2y25amu8j065i" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-617" data-mp="2t20ux6ffzfgc">
                      323 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-618" data-mp="2dz96s6rbfvwf">
                    Review legal agreements
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-619" data-mp="2nwxb9oeq4hrs">
                    Focus exclusively on contracts, NDAs, and formal agreements.
                    Pre-filtered to show only document types classified as legal
                    agreements.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-620" data-mp="2072zla654z94">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-621" data-mp="29x5vbi4ncrb5" />
                </div>
              </Card>

              {/* Action 3 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-622" data-mp="1cgp93o37f8gn">
                <div className="p-6 flex-1" data-id="element-623" data-mp="19g5jb2gjih6j">
                  <div className="flex items-start justify-between mb-4" data-id="element-624" data-mp="19euhv9akhlil">
                    <div className="p-3 bg-amber/10 rounded-xl text-amber group-hover:bg-amber/20 transition-colors" data-id="element-625" data-mp="14hlyj5donbfr">
                      <DollarSignIcon className="w-6 h-6" data-id="element-626" data-mp="1j4x851qb0ld8" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-627" data-mp="1e6lwqmkd22sw">
                      168 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-628" data-mp="liq2doi9kmza">
                    Review flagged financials
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-629" data-mp="vge1eqmwkgn9">
                    Invoices and financial reports that have been flagged by the
                    AI enrichment process for potential discrepancies or
                    required QA.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-630" data-mp="2ef285orj597y">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-631" data-mp="25edq8iz0mops" />
                </div>
              </Card>

              {/* Action 4 */}
              <Card hoverable onClick={onStartReview} className="group flex flex-col h-full" data-id="element-632" data-mp="29f7gvk3uehmd">
                <div className="p-6 flex-1" data-id="element-633" data-mp="2x7wclorj1ixx">
                  <div className="flex items-start justify-between mb-4" data-id="element-634" data-mp="323lc5s3jqzu8">
                    <div className="p-3 bg-gray-500/10 rounded-xl text-gray-400 group-hover:bg-gray-500/20 transition-colors" data-id="element-635" data-mp="2odi01tydsmii">
                      <ListIcon className="w-6 h-6" data-id="element-636" data-mp="2sds594ecn8wb" />
                    </div>
                    <span className="text-xs font-semibold text-amber bg-amber/10 px-2.5 py-1 rounded-full" data-id="element-637" data-mp="3g3w8hanml6ms">
                      1,847 docs
                    </span>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-amber transition-colors" data-id="element-638" data-mp="2w504a1prelmw">
                    Browse all documents
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-639" data-mp="2rbx77jlrwu1y">
                    Open the full document table with no filters applied.
                    Explore the entire production set using the advanced sidebar
                    filters.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-navy-lighter bg-navy/30 flex items-center text-sm font-medium text-gray-400 group-hover:text-amber transition-colors" data-id="element-640" data-mp="3f0zzdle1a5ap">
                  Start Review <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-641" data-mp="3579awghwpuv0" />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>;
}